import { useEffect, useRef, useState } from "react";

/**
 * RemoteCanvas — renders incoming JPEG frames to a canvas and forwards
 * mouse/keyboard input back via the provided sendInput callback.
 */
export default function RemoteCanvas({ ws, sendInput, remoteWidth, remoteHeight }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const lastFrameRef = useRef({ data: null, t: 0 });
  const [fps, setFps] = useState(0);
  const frameCountRef = useRef(0);
  const [showInputHint, setShowInputHint] = useState(true);

  // Set canvas size
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !remoteWidth || !remoteHeight) return;
    c.width = remoteWidth;
    c.height = remoteHeight;
  }, [remoteWidth, remoteHeight]);

  // Compute scale to fit container
  useEffect(() => {
    function update() {
      const cont = containerRef.current;
      if (!cont || !remoteWidth || !remoteHeight) return;
      const cw = cont.clientWidth;
      const ch = cont.clientHeight;
      const s = Math.min(cw / remoteWidth, ch / remoteHeight, 1);
      setScale(s);
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [remoteWidth, remoteHeight]);

  // FPS counter
  useEffect(() => {
    const id = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Handle incoming frames via ws listener override
  useEffect(() => {
    if (!ws) return;
    const handler = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg.type !== "browser.frame") return;
      const { data, format } = msg.payload || {};
      if (!data) return;
      // data is base64-encoded JPEG bytes from Go json marshaling of []byte
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        frameCountRef.current++;
      };
      img.src = `data:image/${format || "jpeg"};base64,${data}`;
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [ws]);

  // Map canvas-local mouse coords to remote pixel coords
  function localToRemote(e) {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / scale);
    const y = Math.round((e.clientY - rect.top) / scale);
    return { x, y };
  }

  function handleMouseMove(e) {
    if (!sendInput) return;
    const { x, y } = localToRemote(e);
    sendInput("browser.input", { kind: "mouse_move", x, y });
  }

  function handleMouseDown(e) {
    if (!sendInput) return;
    e.preventDefault();
    setShowInputHint(false);
    const { x, y } = localToRemote(e);
    sendInput("browser.input", { kind: "mouse_move", x, y });
    sendInput("browser.input", { kind: "mouse_btn", button: e.button, down: true });
  }

  function handleMouseUp(e) {
    if (!sendInput) return;
    e.preventDefault();
    sendInput("browser.input", { kind: "mouse_btn", button: e.button, down: false });
  }

  function handleWheel(e) {
    if (!sendInput) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 120 : -120;
    sendInput("browser.input", { kind: "mouse_wheel", delta });
  }

  function handleKeyDown(e) {
    if (!sendInput) return;
    e.preventDefault();
    sendInput("browser.input", { kind: "key", key_vk: e.keyCode, down: true });
  }

  function handleKeyUp(e) {
    if (!sendInput) return;
    e.preventDefault();
    sendInput("browser.input", { kind: "key", key_vk: e.keyCode, down: false });
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#000",
        outline: "none",
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onContextMenu={(e) => e.preventDefault()}
    >
      <canvas
        ref={canvasRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "center",
          background: "#000",
          imageRendering: "pixelated",
          cursor: "crosshair",
        }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
      />

      {/* FPS overlay */}
      <div style={{
        position: "absolute",
        top: 10, right: 12,
        fontFamily: "var(--mono)",
        fontSize: 10,
        color: "var(--text-dim)",
        background: "rgba(0,0,0,0.6)",
        padding: "3px 8px",
        borderRadius: 4,
        letterSpacing: "0.1em",
      }}>
        {fps} FPS · {remoteWidth}×{remoteHeight}
      </div>

      {showInputHint && (
        <div style={{
          position: "absolute",
          bottom: 16, left: "50%",
          transform: "translateX(-50%)",
          fontFamily: "var(--mono)",
          fontSize: 11,
          color: "var(--acc)",
          background: "rgba(0,0,0,0.7)",
          padding: "8px 14px",
          borderRadius: 4,
          letterSpacing: "0.06em",
          pointerEvents: "none",
        }}>
          Click anywhere to start controlling the remote machine
        </div>
      )}
    </div>
  );
}
